import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Production API URL (hardcoded for reliability)
const API_URL = 'https://api.hitzlink.com/api';

/**
 * Clear stores that are scoped to the logged-in identity. Must run whenever
 * the auth principal changes (logout, impersonate, stop impersonating) so
 * we never leak another user's pages or a stale X-Account-Id header.
 */
async function resetIdentityScopedStores() {
  try {
    const { useBuilderStore } = await import('./builderStore');
    useBuilderStore.getState().clearStore();
  } catch (e) {
    console.error('Failed to clear builder store:', e);
  }
  try {
    const { useAccountStore } = await import('./accountStore');
    useAccountStore.getState().reset();
  } catch (e) {
    console.error('Failed to reset account store:', e);
  }
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  subscription: string;
  subscriptionExpiresAt?: string | null;
  role: string;
  createdAt: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  impersonating: boolean;
  adminBackup: { user: User; token: string } | null;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonating: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      impersonating: false,
      adminBackup: null,

      checkAuth: async () => {
        try {
          const token = get().token;
          if (!token) {
            set({ user: null, isAuthenticated: false });
            return;
          }

          // Verify token with backend
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Token invalid');
          }

          const data = await response.json();
          set({ 
            user: data.user, 
            isAuthenticated: true 
          });
        } catch (error) {
          console.error('Auth check failed:', error);
          
          // Clear invalid token and all stored data
          localStorage.clear();
          sessionStorage.clear();
          
          // Clear auth state
          set({ user: null, isAuthenticated: false, token: null });
          
          // Clear builder store
          try {
            const { useBuilderStore } = await import('./builderStore');
            useBuilderStore.getState().clearStore();
          } catch (e) {
            console.error('Failed to clear builder store:', e);
          }
        }
      },

      // Silently swap the current JWT for a fresh 7-day one.
      //
      // Called on a timer (every hour) and on `visibilitychange` so a
      // tab that's been left open for days never falls off the cliff
      // at the 7-day expiry mark. We don't surface anything to the
      // user when this succeeds — it's invisible session maintenance.
      //
      // On 401 the global interceptor (lib/apiInterceptor.ts) takes
      // over: shows the "session expired" toast and redirects to the
      // login page. Here we just bail out and return false so callers
      // can short-circuit if needed.
      //
      // Returns true on success, false otherwise. Never throws.
      refreshToken: async () => {
        const token = get().token;
        if (!token) return false;

        try {
          // Skip the global interceptor for this specific call by
          // using the path that's in AUTH_BYPASS_PATHS — we want to
          // handle 401 here instead of bouncing the user.
          const response = await fetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            // 401 → token already expired; the next regular API call
            // will trip the global interceptor. Don't double-handle
            // here.
            return false;
          }

          const data = await response.json();
          if (!data?.token) return false;

          set({
            token: data.token,
            user: data.user || get().user,
            isAuthenticated: true,
          });

          // Mirror the same fallback the login path uses.
          try {
            localStorage.setItem('token', data.token);
          } catch {
            try {
              sessionStorage.setItem('token', data.token);
            } catch {
              /* both storages blocked — token stays in memory only */
            }
          }
          return true;
        } catch (e) {
          console.warn('Token refresh failed (likely transient):', e);
          return false;
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        
        try {
          console.log('🚀 Starting login for:', email);
          
          const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.error || 'Login failed');
          }

          console.log('✅ Login successful:', data);

          set({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
          });

          // 🔧 FIX: Manually save token to localStorage (fallback for persist issues)
          try {
            localStorage.setItem('token', data.token);
            console.log('✅ Token manually saved to localStorage');
          } catch (storageError) {
            console.warn('⚠️ localStorage blocked, using sessionStorage:', storageError);
            sessionStorage.setItem('token', data.token);
          }
        } catch (error: any) {
          set({ isLoading: false });
          console.error('❌ Login error:', error);
          throw error;
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true });
        
        try {
          console.log('🚀 Starting registration for:', email);
          
          const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password, name }),
          });

          const data = await response.json();
          
          console.log('📦 Registration response:', data);

          if (!response.ok) {
            throw new Error(data.error || 'Registration failed');
          }

          console.log('✅ Registration successful');

          set({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
          });

          // 🔧 FIX: Manually save token to localStorage (fallback for persist issues)
          try {
            localStorage.setItem('token', data.token);
            console.log('✅ Token manually saved to localStorage');
          } catch (storageError) {
            console.warn('⚠️ localStorage blocked, using sessionStorage:', storageError);
            sessionStorage.setItem('token', data.token);
          }
        } catch (error: any) {
          set({ isLoading: false });
          console.error('❌ Registration error:', error);
          throw error;
        }
      },

      logout: async () => {
        // Clear all stored data
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear auth state
        set({ 
          user: null, 
          isAuthenticated: false, 
          token: null 
        });
        
        // Clear builder store (lazy import to avoid circular dependency)
        const { useBuilderStore } = await import('./builderStore');
        useBuilderStore.getState().clearStore();

        // Drop any team workspace context so the next user starts on
        // their own account (lazy import to avoid circular dependency).
        try {
          const { useAccountStore } = await import('./accountStore');
          useAccountStore.getState().reset();
        } catch (e) {
          console.error('Failed to reset account store:', e);
        }
      },

      updateUser: async (updates: Partial<User>) => {
        const user = get().user;
        const token = get().token;
        
        if (!user || !token) {
          throw new Error('Not authenticated');
        }

        try {
          const response = await fetch(`${API_URL}/auth/me`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updates),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Update failed');
          }

          const data = await response.json();
          
          set({ user: data.user });
        } catch (error) {
          console.error('Update user error:', error);
          throw error;
        }
      },

      impersonate: async (userId: string) => {
        const currentUser = get().user;
        const currentToken = get().token;

        if (!currentUser || !currentToken) {
          throw new Error('Not authenticated');
        }

        try {
          const response = await fetch(`${API_URL}/admin/impersonate/${userId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${currentToken}`,
            },
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to impersonate');
          }

          const data = await response.json();

          // Backup current admin session
          set({
            adminBackup: { user: currentUser, token: currentToken },
            user: data.user,
            token: data.token,
            isAuthenticated: true,
            impersonating: true,
          });

          // Save to localStorage
          try {
            localStorage.setItem('token', data.token);
            localStorage.setItem('adminBackup', JSON.stringify({ user: currentUser, token: currentToken }));
          } catch (e) {
            // silent
          }

          // Drop previous identity's workspace header + cached pages so the
          // dashboard doesn't keep rendering the admin's (or prior user's)
          // list until a manual refresh. Stale X-Account-Id is especially
          // bad: it can make /pages fail and the store then keeps old rows.
          await resetIdentityScopedStores();
        } catch (error) {
          console.error('Impersonate error:', error);
          throw error;
        }
      },

      stopImpersonating: () => {
        const backup = get().adminBackup;
        if (!backup) return;

        set({
          user: backup.user,
          token: backup.token,
          isAuthenticated: true,
          impersonating: false,
          adminBackup: null,
        });

        // Restore admin token in localStorage
        try {
          localStorage.setItem('token', backup.token);
          localStorage.removeItem('adminBackup');
        } catch (e) {
          // silent
        }

        void resetIdentityScopedStores();
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
